import { Accordion, AccordionDetails, Typography, useEventCallback } from '@mui/material';
import { memo, useEffect, useRef, useState } from 'react';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { ExpandMore } from '@mui/icons-material';
import { useFormikContext } from 'formik';
import { AccordionContactsVM, PracticeDetailVM } from 'practice/view-models/practice-detail';
import Form from './form';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { toast } from 'react-toastify';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { useParams } from 'react-router-dom';

interface AccordionContactsProps {
  disabled?: boolean;
}

const AccordionContactsComponent = ({
  disabled = false,
}: AccordionContactsProps): React.ReactElement => {
  const [open, setOpen] = useState(true);

  const params = useParams();

  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    ref.current?.scrollIntoView();
  }, []);

  const formContext = useFormikContext<PracticeDetailVM>();

  const onChange = useEventCallback(
    (key: keyof AccordionContactsVM, value: AccordionContactsVM[keyof AccordionContactsVM]) => {
      formContext.setFieldValue('contacts', {
        ...(formContext.values.contacts || {}),
        [key]: value,
      });
    }
  );

  const updateData = useHttpClient(`${__PRACTICE_API_URL__}/pratiche/contatti/aggiornamento`, {
    options: { method: 'PUT' },
  });

  const onSave = useEventCallback(() => {
    (
      updateData({
        data: {
          email: formContext.values.contacts?.email || undefined,
          pec: formContext.values.contacts?.pec || undefined,
          numTelefono: formContext.values.contacts?.telephone || undefined,
          numCellulare: formContext.values.contacts?.mobilePhone || undefined,
          noteContatto: formContext.values.contacts?.note || undefined,
          key: {
            codiceEnte: params.codiceEnte || undefined,
            tipoRegistro: params.tipoRegistro || undefined,
            idAnnoPratica: params.idAnnoPratica
              ? Number.parseInt(params.idAnnoPratica, 10)
              : undefined,
            progressivoPratica: params.progressivoPratica
              ? Number.parseInt(params.progressivoPratica, 10)
              : undefined,
          },
        },
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
    });
  });

  return (
    <Accordion
      expanded={open}
      onChange={() => {
        setOpen(!open);
      }}
      ref={ref}
    >
      <AccordionSummary
        aria-controls="accordion-contacts-content"
        id="accordion-contacts-header"
        expandIcon={<ExpandMore />}
      >
        <Typography>Contatti</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Form
          onChange={onChange}
          disabled={disabled}
          values={formContext.values?.contacts}
          onSave={onSave}
        />
      </AccordionDetails>
    </Accordion>
  );
};

AccordionContactsComponent.displayName = 'Practice Detail - Form - accordion contacts';

const AccordionContacts = memo(AccordionContactsComponent);

export default AccordionContacts;
